import React from 'react';
import { Drawer, Pagination } from '@mui/material';
import { ChatHeader } from 'components/ChatComponents/ChatHeader';
import { useTranslation } from 'react-i18next';
import { NotificationList } from './NotificationList';
import { Notifications as NotificationIcon } from '@mui/icons-material';
import { RootStates } from 'redux/store';
import { useSelector } from 'react-redux';
import './Notifications.scss';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormSelect } from 'components/common/FormSelect';
import {
  useGetNotificationsMutation,
  useMarkAllNotificationAsReadMutation,
} from 'redux/services/project';
import { Loader } from 'components/common/Loader';

export type NotificationsProps = {
  open: boolean;
  onClose: () => void;
};

export const Notifications: React.FC<NotificationsProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { projectId, setupId } = useParams();
  const projects = useSelector((state: RootStates) => state.project.projects);
  const [getNotifications, { data, isLoading }] = useGetNotificationsMutation();
  const [markAllAsRead] = useMarkAllNotificationAsReadMutation();
  const [selectedProject, setSelectedProject] = useState<any>(null);

  useEffect(() => {
    getNotifications({
      projectId: projectId || setupId,
      notificationType: 'project',
    });
  }, [getNotifications, projectId, setupId]);

  useEffect(() => {
    if (projects.length && (projectId || setupId)) {
      const pId = projectId || setupId;
      const p = projects.find((p) => p.projectInfo.id === pId);

      if (p) {
        setSelectedProject({
          ...p,
          id: p.projectInfo.projectId,
          name: p.projectInfo.projectName,
        });
      }
    } else {
      setSelectedProject(null);
    }
  }, [projectId, setupId, projects]);

  useEffect(() => {
    if (data?.data?.length > 0 && data?.data?.some((n: any) => n.hasRead)) {
      markAllAsRead({});
    }
  }, [data, markAllAsRead]);

  const handlePaginationChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      if (value !== data?.pagination?.page) {
        getNotifications({
          projectId: selectedProject?.projectInfo.id,
          page: value,
          notificationType: 'project',
        });
      }
    },
    [data, selectedProject, getNotifications],
  );

  const handleSelectProject = (project: any) => {
    getNotifications({
      projectId: project?.projectInfo.id,
    });
    setSelectedProject(project);
  };

  const pagination = React.useMemo(() => {
    if (data?.pagination?.totalPage >= 1) {
      return (
        <Pagination
          className="align-self-end my-1"
          disabled={isLoading}
          count={data?.pagination?.totalPage ?? 0}
          page={Number(data?.pagination?.page ?? 1)}
          color="primary"
          shape="rounded"
          onChange={handlePaginationChange}
        />
      );
    }

    return null;
  }, [data, isLoading, handlePaginationChange]);

  const handleClose = () => {
    setSelectedProject(null);
    onClose();
  };

  return (
    <Drawer
      className="notification-drawer"
      open={open}
      anchor="right"
      PaperProps={{
        className: 'notification-window',
      }}
      keepMounted={false}
      onClose={handleClose}
    >
      {isLoading && <Loader />}
      <ChatHeader
        icon={<NotificationIcon sx={{ color: '#fff' }} />}
        title={t('label_notifications')}
        onClose={onClose}
      />
      <div className="d-flex flex-column w-100 justify-content-end p-1">
        <FormSelect
          className="notification__project-select"
          placeholder="Select Project"
          selectedItem={selectedProject}
          items={projects
            ?.filter((p) => p.isLaunched)
            .map((p) => ({
              ...p,
              id: p.projectInfo.projectId,
              name: p.projectInfo.projectName,
            }))}
          uniqueId="id"
          onSelect={handleSelectProject}
        />
      </div>
      <div className="notifications d-flex p-2 w-100 h-100 overflow-auto">
        <NotificationList
          notifications={data?.data.length > 0 ? data.data : []}
        />
      </div>
      {pagination}
    </Drawer>
  );
};
